import type { EventNode } from "../types";

/**
 * Find the IDs of all ancestors of `eventId` in the EventNode tree.
 * Returns an empty array if `eventId` is not found.
 * Order: outermost ancestor first.
 */
export function findAncestorIds(nodes: EventNode[], eventId: string): string[] {
  const path: string[] = [];
  const found = walk(nodes, eventId, path);
  return found ? path : [];

  function walk(nodes: EventNode[], target: string, path: string[]): boolean {
    for (const n of nodes) {
      if (n.id === target) return true;
      if (n.children.length > 0) {
        path.push(n.id);
        if (walk(n.children, target, path)) return true;
        path.pop();
      }
    }
    return false;
  }
}

/**
 * Collapsed ancestor ids on the path to `targetId`, outermost first.
 *
 * The target's own collapsed state is irrelevant — a collapsed node still
 * has a row of its own; only collapsed ancestors remove it from the
 * flattened list. Empty when the target is missing or fully visible.
 */
export function findCollapsedAncestors(
  eventNodes: EventNode[],
  targetId: string,
  collapsed: Record<string, boolean> | null
): string[] {
  return findAncestorIds(eventNodes, targetId).filter(
    (id) => collapsed?.[id] === true
  );
}

export interface TreeNodeVisitor {
  visit: (node: EventNode, parent?: EventNode) => EventNode[];
  flush?: () => EventNode[];
}

/**
 * Flatten the tree structure into a flat array of EventNode objects
 * Each node in the result will have its children set properly
 * @param eventNodes - The event nodes to flatten
 * @param collapsed - Record indicating which nodes are collapsed
 * @param visitors - Array of visitors to apply to each node
 * @param parentNode - The parent node of the current nodes being processed
 * @returns An array of EventNode objects
 */
export const flatTree = (
  eventNodes: EventNode[],
  collapsed: Record<string, boolean> | null,
  visitors?: TreeNodeVisitor[],
  parentNode?: EventNode
): EventNode[] => {
  const result: EventNode[] = [];
  for (const node of eventNodes) {
    if (visitors && visitors.length > 0) {
      let pendingNodes: EventNode[] = [{ ...node }];

      for (const visitor of visitors) {
        const allResults: EventNode[] = [];
        for (const pendingNode of pendingNodes) {
          const visitorResult = visitor.visit(pendingNode);
          if (parentNode) {
            parentNode.children = visitorResult;
          }
          allResults.push(...visitorResult);
        }
        pendingNodes = allResults;
      }

      for (const pendingNode of pendingNodes) {
        const children = flatTree(
          pendingNode.children,
          collapsed,
          visitors,
          pendingNode
        );
        pendingNode.children = children;
        result.push(pendingNode);
        if (collapsed === null || collapsed[pendingNode.id] !== true) {
          // push one-by-one: spreading tens of thousands of children as
          // call arguments overflows the stack on very large transcripts
          for (const child of children) result.push(child);
        }
      }

      for (const visitor of visitors) {
        if (visitor.flush) {
          const finalNodes = visitor.flush();
          result.push(...finalNodes);
        }
      }
    } else {
      result.push(node);
      const children = flatTree(node.children, collapsed, visitors, node);
      if (collapsed === null || collapsed[node.id] !== true) {
        for (const child of children) result.push(child);
      }
    }
  }

  return result;
};
